/**
 * TerminalFrame.ts — "The Frost Citadel" themed CSS frame for Gallowcrest.
 *
 * Renders a full 4-sided frost/rune-themed border around the xterm.js terminal.
 * Pure CSS/HTML overlay — no canvas, no WebGL, no interference with terminal input.
 *
 * Design: Ice-encrusted fortress + rune-carved sanctum + frozen throne room.
 * Top bar: frost-stone gradient with rune snowflake emblem, hero/class/chapter info.
 * Bottom bar: location + frostbite/corruption status.
 * Side borders: cracked-ice texture via repeating-linear-gradient.
 * Corner brackets: ice-crystal L-shapes with cold blue glow.
 * Rivets: frost spheres with radial-gradient (icy blue center, dark edge).
 * Inner shadow: cold blue vignette inset.
 */

const FRAME_STYLE_ID = 'terminal-frame-styles';

// ── Color Palette ──────────────────────────────────────────────
const FROST_STONE = '#2a2a3a';
const FROST_STONE_DIM = '#1a1a28';
const ICE_BLUE = '#4a9eff';
const ICE_BLUE_DIM = '#2a6ecc';
const FROST_SILVER = '#c0c8d8';
const RUNE_GOLD = '#d4a520';
const DEEP_VOID = '#0a0a0e';
const RIVET_COLOR = '#6a8aaa';

// ── Dimensions ─────────────────────────────────────────────────
const TOP_HEIGHT = 38;
const BOTTOM_HEIGHT = 30;
const SIDE_WIDTH = 10;

const FRAME_CSS = `
/* ── Terminal Frame: The Frost Citadel ─────────────────────── */

#terminal-container {
  position: relative;
}

/* ── Outer frame wrapper ──────────────────────────────────── */
.frost-citadel-frame {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 20;
}

/* ── Top bar: frost-stone info strip ────────────────────────── */
.fc-top-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${TOP_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${SIDE_WIDTH + 8}px;
  background: linear-gradient(180deg, ${FROST_STONE} 0%, ${DEEP_VOID} 80%, #060608 100%);
  border-bottom: 2px solid ${ICE_BLUE_DIM};
  font-family: 'Courier New', monospace;
  user-select: none;
  box-shadow: inset 0 1px 0 rgba(74, 158, 255, 0.12), 0 2px 8px rgba(0, 0, 0, 0.5);
}

.fc-top-bar .fc-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fc-top-bar .fc-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Rune snowflake emblem — CSS hexagonal crystal */
.fc-emblem {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${ICE_BLUE};
  background: radial-gradient(circle, ${FROST_STONE} 30%, ${DEEP_VOID} 70%);
  flex-shrink: 0;
  box-shadow: 0 0 6px rgba(74, 158, 255, 0.3);
  position: relative;
}

/* Snowflake cross inside emblem */
.fc-emblem::before {
  content: '\\2726';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${ICE_BLUE};
  font-size: 12px;
  text-shadow: 0 0 4px rgba(74, 158, 255, 0.6);
}

.fc-title {
  color: ${ICE_BLUE};
  font-weight: bold;
  text-transform: uppercase;
  font-size: 13px;
  letter-spacing: 2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5), 0 0 8px rgba(74, 158, 255, 0.15);
}

.fc-subtitle {
  color: ${FROST_SILVER};
  font-size: 11px;
  letter-spacing: 1px;
  opacity: 0.7;
}

.fc-hero-info {
  color: #8090a8;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.fc-hero-info .fc-highlight {
  color: ${RUNE_GOLD};
}

.fc-chapter-info {
  color: #607088;
  font-size: 11px;
  letter-spacing: 0.5px;
}

/* ── Bottom bar: status strip ──────────────────────────────── */
.fc-bottom-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${BOTTOM_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${SIDE_WIDTH + 8}px;
  background: linear-gradient(0deg, ${FROST_STONE} 0%, ${DEEP_VOID} 80%, #060608 100%);
  border-top: 2px solid ${ICE_BLUE_DIM};
  font-family: 'Courier New', monospace;
  user-select: none;
  box-shadow: inset 0 -1px 0 rgba(74, 158, 255, 0.12), 0 -2px 8px rgba(0, 0, 0, 0.5);
}

.fc-bottom-text {
  color: #6a7a90;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.fc-bottom-text .fc-highlight {
  color: ${ICE_BLUE};
}

/* ── Side borders: cracked ice texture ──────────────────────── */
.fc-side-left,
.fc-side-right {
  position: absolute;
  top: ${TOP_HEIGHT}px;
  bottom: ${BOTTOM_HEIGHT}px;
  width: ${SIDE_WIDTH}px;
  z-index: 21;
  pointer-events: none;
}

.fc-side-left {
  left: 0;
  background:
    repeating-linear-gradient(180deg,
      transparent 0px,
      transparent 4px,
      rgba(74, 158, 255, 0.08) 4px,
      rgba(74, 158, 255, 0.08) 5px
    ),
    linear-gradient(90deg, ${FROST_STONE_DIM} 0%, ${DEEP_VOID} 70%, #060608 100%);
  border-right: 1px solid ${ICE_BLUE_DIM};
  box-shadow: inset -2px 0 6px rgba(0, 0, 0, 0.4);
}

.fc-side-right {
  right: 0;
  background:
    repeating-linear-gradient(180deg,
      transparent 0px,
      transparent 4px,
      rgba(74, 158, 255, 0.08) 4px,
      rgba(74, 158, 255, 0.08) 5px
    ),
    linear-gradient(270deg, ${FROST_STONE_DIM} 0%, ${DEEP_VOID} 70%, #060608 100%);
  border-left: 1px solid ${ICE_BLUE_DIM};
  box-shadow: inset 2px 0 6px rgba(0, 0, 0, 0.4);
}

/* ── Corner brackets: ice-crystal L-shapes ──────────────────── */
.fc-corner {
  position: absolute;
  width: 18px;
  height: 18px;
  z-index: 23;
  pointer-events: none;
}

.fc-corner-tl {
  top: ${TOP_HEIGHT - 2}px;
  left: ${SIDE_WIDTH - 2}px;
  border-left: 3px solid ${ICE_BLUE};
  border-top: 3px solid ${ICE_BLUE};
  border-radius: 2px 0 0 0;
  box-shadow: -1px -1px 6px rgba(74, 158, 255, 0.3);
}

.fc-corner-tr {
  top: ${TOP_HEIGHT - 2}px;
  right: ${SIDE_WIDTH - 2}px;
  border-right: 3px solid ${ICE_BLUE};
  border-top: 3px solid ${ICE_BLUE};
  border-radius: 0 2px 0 0;
  box-shadow: 1px -1px 6px rgba(74, 158, 255, 0.3);
}

.fc-corner-bl {
  bottom: ${BOTTOM_HEIGHT - 2}px;
  left: ${SIDE_WIDTH - 2}px;
  border-left: 3px solid ${ICE_BLUE};
  border-bottom: 3px solid ${ICE_BLUE};
  border-radius: 0 0 0 2px;
  box-shadow: -1px 1px 6px rgba(74, 158, 255, 0.3);
}

.fc-corner-br {
  bottom: ${BOTTOM_HEIGHT - 2}px;
  right: ${SIDE_WIDTH - 2}px;
  border-right: 3px solid ${ICE_BLUE};
  border-bottom: 3px solid ${ICE_BLUE};
  border-radius: 0 0 2px 0;
  box-shadow: 1px 1px 6px rgba(74, 158, 255, 0.3);
}

/* ── Corner rivets: frost-crystal spheres ───────────────────── */
.fc-rivet {
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, ${FROST_SILVER} 0%, ${RIVET_COLOR} 60%, ${FROST_STONE} 100%);
  z-index: 24;
  pointer-events: none;
  box-shadow: 0 0 3px rgba(74, 158, 255, 0.2);
}

.fc-rivet-tl { top: ${TOP_HEIGHT + 4}px; left: ${SIDE_WIDTH + 2}px; }
.fc-rivet-tr { top: ${TOP_HEIGHT + 4}px; right: ${SIDE_WIDTH + 2}px; }
.fc-rivet-bl { bottom: ${BOTTOM_HEIGHT + 4}px; left: ${SIDE_WIDTH + 2}px; }
.fc-rivet-br { bottom: ${BOTTOM_HEIGHT + 4}px; right: ${SIDE_WIDTH + 2}px; }

/* ── Inner depth shadow: cold blue vignette ─────────────────── */
.fc-inner-shadow {
  position: absolute;
  top: ${TOP_HEIGHT}px;
  left: ${SIDE_WIDTH}px;
  right: ${SIDE_WIDTH}px;
  bottom: ${BOTTOM_HEIGHT}px;
  pointer-events: none;
  z-index: 19;
  box-shadow: inset 0 0 20px rgba(74, 158, 255, 0.15),
              inset 0 0 4px rgba(0, 0, 0, 0.6);
}

/* ── Ensure xterm fills the middle area ──────────────────────── */
.frost-citadel-active #terminal-container {
  padding: ${TOP_HEIGHT}px ${SIDE_WIDTH}px ${BOTTOM_HEIGHT}px ${SIDE_WIDTH}px;
}

.frost-citadel-active #terminal-container .xterm {
  height: 100% !important;
}
`;

export interface FrameInfo {
  heroName?: string;
  className?: string;
  chapter?: string;
  location?: string;
  status?: string;
}

export class TerminalFrame {
  private container: HTMLElement;
  private frameEl: HTMLElement | null = null;
  private topBar: HTMLElement | null = null;
  private bottomBar: HTMLElement | null = null;
  private enabled = false;
  private info: FrameInfo = {};

  /** CSS frame dimensions in pixels — used by FrameRenderer to adjust layout */
  static readonly TOP_HEIGHT = TOP_HEIGHT;
  static readonly BOTTOM_HEIGHT = BOTTOM_HEIGHT;
  static readonly SIDE_WIDTH = SIDE_WIDTH;

  constructor(container: HTMLElement) {
    this.container = container;
    this.injectStyles();
  }

  private injectStyles(): void {
    if (document.getElementById(FRAME_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = FRAME_STYLE_ID;
    style.textContent = FRAME_CSS;
    document.head.appendChild(style);
  }

  /** Enable the Frost Citadel frame. */
  enable(): void {
    if (this.enabled) return;
    this.buildFrame();
    document.body.classList.add('frost-citadel-active');
    this.enabled = true;
  }

  /** Disable the frame. */
  disable(): void {
    if (!this.enabled) return;
    this.destroyFrame();
    document.body.classList.remove('frost-citadel-active');
    this.enabled = false;
  }

  /** Toggle frame on/off. Returns new state. */
  toggle(): boolean {
    if (this.enabled) {
      this.disable();
    } else {
      this.enable();
    }
    return this.enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Update the info displayed in the top bar. */
  updateInfo(info: Partial<FrameInfo>): void {
    this.info = { ...this.info, ...info };
    if (this.enabled) this.renderTopBar();
  }

  /** Update the bottom bar text. Called by FrameRenderer to route footer content. */
  updateBottomBar(text: string): void {
    if (!this.bottomBar) return;
    const el = this.bottomBar.querySelector('.fc-bottom-text');
    if (el) {
      el.innerHTML = this.escapeHtml(text);
    }
  }

  private buildFrame(): void {
    this.frameEl = document.createElement('div');
    this.frameEl.className = 'frost-citadel-frame';

    // Top bar
    this.topBar = document.createElement('div');
    this.topBar.className = 'fc-top-bar';
    this.frameEl.appendChild(this.topBar);

    // Bottom bar
    this.bottomBar = document.createElement('div');
    this.bottomBar.className = 'fc-bottom-bar';
    this.bottomBar.innerHTML = '<span class="fc-bottom-text"></span>';
    this.frameEl.appendChild(this.bottomBar);

    // Side borders
    const sideL = document.createElement('div');
    sideL.className = 'fc-side-left';
    this.frameEl.appendChild(sideL);

    const sideR = document.createElement('div');
    sideR.className = 'fc-side-right';
    this.frameEl.appendChild(sideR);

    // Inner depth shadow
    const innerShadow = document.createElement('div');
    innerShadow.className = 'fc-inner-shadow';
    this.frameEl.appendChild(innerShadow);

    // Corner brackets
    for (const pos of ['tl', 'tr', 'bl', 'br']) {
      const corner = document.createElement('div');
      corner.className = `fc-corner fc-corner-${pos}`;
      this.frameEl.appendChild(corner);
    }

    // Corner rivets
    for (const pos of ['tl', 'tr', 'bl', 'br']) {
      const rivet = document.createElement('div');
      rivet.className = `fc-rivet fc-rivet-${pos}`;
      this.frameEl.appendChild(rivet);
    }

    this.container.appendChild(this.frameEl);
    this.renderTopBar();
  }

  private destroyFrame(): void {
    if (this.frameEl) {
      this.frameEl.remove();
      this.frameEl = null;
      this.topBar = null;
      this.bottomBar = null;
    }
  }

  private renderTopBar(): void {
    if (!this.topBar) return;

    let heroHtml = '';
    if (this.info.heroName) {
      const parts: string[] = [];
      parts.push(`<span class="fc-highlight">${this.escapeHtml(this.info.heroName)}</span>`);
      if (this.info.className) {
        parts.push(this.escapeHtml(this.info.className));
      }
      heroHtml = `<span class="fc-hero-info">${parts.join(' · ')}</span>`;
    }

    let chapterHtml = '';
    if (this.info.chapter) {
      chapterHtml = `<span class="fc-chapter-info">${this.escapeHtml(this.info.chapter)}</span>`;
    }

    this.topBar.innerHTML = `
      <span class="fc-left">
        <span class="fc-emblem"></span>
        <span class="fc-title">Gallowcrest</span>
        <span class="fc-subtitle">ChoiceRealms</span>
      </span>
      <span class="fc-right">
        ${heroHtml}
        ${chapterHtml}
        <span class="fc-chapter-info">${this.info.status || ''}</span>
      </span>
    `;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
